import React, { createContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { APIRouteConstants, authRouteConstants } from 'constants/routeConstants';

import axiosInstance from "configs/axiosConfig";
import { get } from 'lodash';
import { clearOfflineData } from 'utils/offlineSync';
import { fetchMerchantProfileShared } from 'utils/auth';
import { extractCursor } from 'utils/pagination';
import { logFrontendError } from 'utils/errorLogger';
export const EcommerceContext = createContext();

// Most calls in this provider use the raw `axios` package (with an absolute API_URL)
// rather than the app's axiosInstance, so they never hit the axiosConfig interceptor
// and their failures are otherwise invisible. Route the POS-critical ones (payment,
// order placement, terminal, cart) through the shared Firestore logger with a stable
// source + operation label. Non-fatal by nature — never let logging throw.
const reportContextError = (operation, err, severity = "error") => {
    try {
        const detail =
            err?.response?.data?.detail ||
            err?.response?.data?.error ||
            err?.message ||
            String(err);
        logFrontendError({
            message: `[UserSideContext] ${operation}: ${detail}`,
            stack: err?.stack,
            source: "userside-context",
            severity,
        });
    } catch {
        /* logging must never break the flow it reports on */
    }
};

export const EcommerceProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [paymentMode, setPaymentMode] = useState(null);
    const [terminals, setTerminals] = useState(null);
    const [selectedTerminal, setSelectedTerminals] = useState(null);
    const [sticittTerminals, setSticittTerminals] = useState(null);
    const [token, setToken] = useState("");
    const [menuItems, setMenuItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesFailed, setCategoriesFailed] = useState(false);
    const [categoriesItems, setCategoriesItems] = useState([]);
    const [cart, setCart] = useState([]);
    const [cartSummery, setCartSummery] = useState();
    const [cartAllitems, setCartAllitems] = useState();
    const [cartItemsTotalVat, setCartItemsTotalVat] = useState(0);
    const [orders, setOrders] = useState([]);
    const [hasRedirected, setHasRedirected] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [nextCursor, setNextCursor] = useState(null);
    const [prevCursor, setPrevCursor] = useState(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const [socketData, setSocketData] = useState(null);
    const [isTerminalOnline, setisTerminalOnline] = useState(null);
    const [tips, setTips] = useState("");
    const [isTipFeatureEnabled, setIsTipFeatureEnabled] = useState(false);
    const [isTipFeatureEnabledPOS, setIsTipFeatureEnabledPOS] = useState(false);
    const [merchantName, setMerchantName] = useState("");
    const [appliedVoucher, setAppliedVoucher] = useState("");
    const [zapperCredential, setZapperCredential] = useState(null);
    const [vatRegNo, setVatRegNo] = useState("");
    // console.log("cartAllitems",cartAllitems)
    // Shared merchant/session data (profile, categories, payment methods,
    // terminals, tip settings) is requested independently by whichever
    // page/component happens to mount — Home, the POS tab, various report
    // pages all call these same context functions on their own. Without
    // this, each navigation re-fires the same handful of network calls.
    // inFlightRef tracks a promise per key so a second caller within the
    // same in-flight window gets the pending result instead of starting a
    // new request; it clears once settled so a genuinely later, deliberate
    // refresh (e.g. PaymentOptions re-pulling the terminal list) still works.
    const inFlightRef = useRef({});
    const dedupeFetch = (key, run) => {
        if (inFlightRef.current[key]) return inFlightRef.current[key];
        const promise = run().finally(() => {
            inFlightRef.current[key] = null;
        });
        inFlightRef.current[key] = promise;
        return promise;
    };
    // For merchant-level config that doesn't change mid-session (categories,
    // payment methods, Zapper credentials, tip/VAT settings) — attempt the
    // fetch at most once per session rather than once per caller, regardless
    // of how many components independently ask for it or how far apart in
    // time those asks land.
    const fetchedOnceRef = useRef({});
    const fetchOncePerSession = (key, run) => {
        if (fetchedOnceRef.current[key]) return Promise.resolve();
        fetchedOnceRef.current[key] = true;
        return dedupeFetch(key, run);
    };
    const host = window.location.host;
    const [loading, setLoading] = useState(false);
    // || host.includes('rolemanagement-tau.vercel.app')
    // || host.includes('rolemanagement-tau.vercel.app')
    console.log("host", host)
    // alert(host)
    const API_URL =
        host.includes("print-implementation.vercel.app") || host.includes("localhost:3001") || host.includes("localhost:3002") || host.includes("beta.webbieshop.com") || host.includes('webbie-merchant-terminal.vercel.app') || host.includes('webbie-merchant-test.vercel.app') || host.includes('webbie-frontend-test.vercel.app') || host.includes('shipping-merchant.vercel.app') || host.includes('rolemanagement-tau.vercel.app')

            ? "https://beta-api.webbieshop.com/customer/"
            : "https://api.webbieshop.com/customer/";
    const API_URL_FOR_Terminal =
        host.includes("print-implementation.vercel.app") || host.includes("localhost:3001") || host.includes("localhost:3002") || host.includes("beta.webbieshop.com") || host.includes('webbie-merchant-terminal.vercel.app') || host.includes('webbie-merchant-test.vercel.app') || host.includes('webbie-frontend-test.vercel.app') || host.includes('shipping-merchant.vercel.app') || host.includes('rolemanagement-tau.vercel.app')

            ? "https://beta-api.webbieshop.com/"
            : "https://api.webbieshop.com/";

    // console.log("Using API URL:", host);   

    // ------------------ AUTH ------------------
    const getUser = async () => {
        const savedAuthToken = localStorage.getItem("authToken");
        if (!savedAuthToken || user) return;
        return dedupeFetch("user", async () => {
            try {
                let res = await fetchMerchantProfileShared(axiosInstance, APIRouteConstants.AUTH.ME);
                if (res === false) {
                    if (!navigator.onLine) return;
                    if (!hasRedirected) {
                        setHasRedirected(true);
                        setIsTipFeatureEnabled(false);
                        clearOfflineData();
                        localStorage.removeItem("access-token");
                        localStorage.removeItem("refresh-token");
                        window.location.href = authRouteConstants.LOGIN;
                    }
                    return;
                }

                let { data, response } = res;
                if (data && data.success) {
                    const user = data.data;
                    setUser(user);
                    setIsTipFeatureEnabledPOS(user?.tip_enabled);
                    setIsTipFeatureEnabled(user?.tip_enabled);
                }
            } catch (err) {
                console.error('Login Error:', err);
                if (!navigator.onLine) return;
                reportContextError("getUser", err);
                if (!hasRedirected) {
                    setHasRedirected(true);
                    clearOfflineData();
                    localStorage.removeItem("access-token");
                    localStorage.removeItem("refresh-token");
                    window.location.href = authRouteConstants.LOGIN;
                }
            }
        });
    };
    const getPaymentMethods = async () => {
        return fetchOncePerSession("paymentMethods", async () => {
            try {
                let res = await axiosInstance.get(APIRouteConstants.STORE.ALL_PAYMENT_MODES, {
                    offlineCache: true,
                });
                if (res === false) {
                    return;
                }

                let { data, response } = res;
                if (data) {
                    const user = data;
                    setPaymentMode(user);
                    const filtered = user.filter(
                        (method) =>
                           ( method.payment_method_name?.includes("Zapper") || method.payment_method_name?.includes("Terminal")) &&
                            method.is_pos_activated === true,
                    );

                    setTerminals(filtered)
                }
            } catch (err) {
                console.error('Login Error:', err);
            }
        });
    };

    const fetchTipSettings = async () => {
        return fetchOncePerSession("tipSettings", async () => {
            try {
                let res = await axiosInstance.get(APIRouteConstants.WEBSITE.WEB_ABOUT_US);
                if (res && res.data) {
                    // setIsTipFeatureEnabled(res.data.tip_enabled || false);
                    setVatRegNo(res.data.vat_registration_no || "");
                }
            } catch (err) {
                console.error('Error fetching Tips in Payment settings:', err);
            }
        });
    };

    const getZapperPaymentCredentials = async () => {
        return fetchOncePerSession("zapperCredentials", async () => {
            try {
                let res = await axiosInstance.get(APIRouteConstants.STORE.GET_CREDENTIALS, {
                    params: {
                        gateway_name: "Zapper",
                    },
                });

                if (res === false) {
                    return null;
                }

                const { data } = res;
                if (data) {
                    setZapperCredential(data);
                    return data;
                }
                return null;
            } catch (err) {
                console.error("Error fetching Zapper credentials:", err);
                return null;
            }
        });
    };

    const zapperStatusUpdate = async ({ paidAmount, reference, status, zapperId }) => {
        try {
            const res = await axios.post(
                `${API_URL_FOR_Terminal}${APIRouteConstants.STORE.ZAPPER_STATUS_UPDATE}`,
                { paidAmount, reference, status, zapperId },
                getAuthHeaders()
            );
            return res?.data || null;
        } catch (err) {
            console.error("Zapper status update failed:", err);
            reportContextError("zapperStatusUpdate", err);
            return null;
        }
    };

    const zapperSaveData = async ({ paidAmount, reference, status, zapperId }) => {
        try {
            const res = await axios.post(
                `${API_URL_FOR_Terminal}${APIRouteConstants.STORE.ZAPPER_SAVE_DATA}`,
                { paidAmount, reference, status, zapperId },
                getAuthHeaders()
            );
            return res?.data || null;
        } catch (err) {
            console.error("Zapper save data failed:", err);
            reportContextError("zapperSaveData", err);
            return null;
        }
    };

    const updateTipSettings = async (enabled) => {
        try {
            const res = await axiosInstance.put(APIRouteConstants.WEBSITE.WEB_ABOUT_US, {
                tip_enabled: enabled
            });
            if (res && res.data) {
                setIsTipFeatureEnabled(enabled);
                setIsTipFeatureEnabledPOS(enabled);
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error updating Tips in Payment settings:', err);
            return false;
        }
    };
    const SticittTerminalProceedtoPayment = async (
        datas,
        tid,
        setPaymentDialogStatus,
        paymentid,
        ispaymentavial
    ) => {
        try {
            setisTerminalOnline(null)
            const payload = {
                terminal_id: tid,
                payment_id: ispaymentavial ? paymentid : datas?.data?.payment_id
            };

            // console.log("terminalId", payload);

            let res = await axios.post(`${API_URL_FOR_Terminal}${APIRouteConstants.STORE.STICITT_TERMINAL_PAYMENT}`, {
                terminal_id: tid,
                payment_id: ispaymentavial ? paymentid : datas?.data?.payment_id
            }, getAuthHeaders());

            // let res = await axiosInstance.post(
            //     APIRouteConstants.STORE.STICITT_TERMINAL_PAYMENT,
            //     payload
            // );

            // console.log("proceedtopaymentsticitt", res?.status);
            // if (!res) {
            //     if (typeof onFailure === "function") onFailure();
            //     return;
            // }

            let { data } = res;

            // =============================
            //      CHECK SUCCESS
            // =============================
            if (data?.success === true || res?.status === 200) {
                // Call your OS updates
                // localStorage.removeItem("transaction_id");

                // fetchAllCartItems();
                // fetchCartItemsPrice();

                // setPaymentDialogStatus('success');

            } else if (res?.status === '403' || res?.status === 403) {
                // 🔥 Trigger popup failed
                // setPaymentDialogStatus('failed');
            } else {
                // setPaymentDialogStatus('failed');
            }
            console.log("proceedtopaymentsticitt", res);

        } catch (err) {
            console.error("Payment Error:", err?.response?.data?.error || err.message);
            reportContextError("SticittTerminalProceedtoPayment", err);
            // console.log("proceedtopaymentsticitt", err);
            // 🔥 Trigger popup failed
            // setPaymentDialogStatus('failed');
            setisTerminalOnline(err?.response?.data?.error || err.message);

        }
        //  finally {
        //     // 🔥 ALWAYS RUNS
        //     if (typeof onFinally === "function") setPaymentDialogStatus();
        // }
    };

    const getTerminalList = async () => {
        return dedupeFetch("terminalList", async () => {
            try {
                let res = await axios.get(`${API_URL_FOR_Terminal}${APIRouteConstants.STORE.TERMINAL_LIST}`, getAuthHeaders());
                // let res = await axiosInstance.get(APIRouteConstants.STORE.TERMINAL_LIST);
                if (res === false) {
                    return;
                }

                // console.log("terminalList",res)
                let { data, response } = res;
                if (data) {
                    setSticittTerminals(data)
                }
            } catch (err) {
                console.error('Login Error:', err);
                reportContextError("getTerminalList", err, "warning");
            }
        });
    };

    const saveLastUsedTerminal = async (terminalId) => {
        try {
            await axios.post(
                `${API_URL_FOR_Terminal}${APIRouteConstants.STORE.SAVE_LAST_USED_TERMINAL}`,
                { terminal_id: terminalId },
                getAuthHeaders()
            );
        } catch (err) {
            console.error('Save last used terminal error:', err);
            reportContextError("saveLastUsedTerminal", err, "warning");
        }
    };

    const login = async () => {
        try {
            const merchantToken = localStorage.getItem('access-token');
            if (!merchantToken) return;
            const res = await axiosInstance.post(APIRouteConstants.AUTH.MERCHANT_USER);
            // res is false when offline (network error) — keep existing session, don't log out
            if (res === false) return;

            const newAuthToken = res?.data?.access;
            if (newAuthToken) {
                setToken(newAuthToken);
                localStorage.setItem('authToken', newAuthToken);
            }
        } catch (error) {
            console.error('Login Error:', error);
            // 401/403 are already handled by the axios interceptor (redirect to login).
            // Any other error here (e.g. unexpected server failure) should not log the user out.
            reportContextError("login", error, "warning");
        }
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('authToken');
    };

    // ------------------ AUTH HEADERS ------------------
    const getDomainHeader = () => {
        return user?.websiteUrl ? `${user.websiteUrl}.webbieshop.com` : '';
    };
    // console.log("getDomainHeader:", getDomainHeader());
    const getAuthHeaders = () => {
        const authToken = token || localStorage.getItem('authToken');
        return {
            headers: {
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                Accept: "application/json",
                'domain-name': getDomainHeader(),

            },
        };
    };

    // For confirmed-public storefront endpoints (categories, product search, etc.)
    // — never attach a token, even if one is available.
    const getPublicHeaders = () => ({
        headers: {
            Accept: "application/json",
            'domain-name': getDomainHeader(),
        },
    });

    // ------------------ MENU ------------------
    const fetchMenuItems = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/api/menu`, getAuthHeaders());
            setMenuItems(res.data);
        } catch (error) {
            console.error('Fetch Menu Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        return fetchOncePerSession("categories", async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_URL}api/categories/`, getPublicHeaders());
                setCategories(res.data);
                setCategoriesFailed(false);
            } catch (error) {
                console.error('Fetch Categories Error:', error);
                setCategoriesFailed(true);
            } finally {
                setLoading(false);
            }
        });
    };

    // fetchOncePerSession latches after the first attempt, success or failure,
    // so a failed load needs its own path back in rather than just calling
    // fetchCategories() again (which would resolve immediately as a no-op).
    const retryCategories = async () => {
        fetchedOnceRef.current["categories"] = false;
        await fetchCategories();
    };

    const fetchCategoriesItemsByname = async (category_name, cursor = null, setLoader, query = "") => {
        setLoading(true);
        const params = { cursor: cursor || undefined };
        if (category_name) params.category_name = category_name;
        if (query) params.query = query;
        const res = await axios.get(`${API_URL}api/selected-product-search/`, { ...getPublicHeaders(), params });
        setLoading(false);
        if (!res) return;
        const { data } = res;
        if (data) {
            setCategoriesItems(data.results);
            setHasNextPage(!!data.next);
            setHasPreviousPage(!!data.previous);
            setNextCursor(extractCursor(data.next));
            setPrevCursor(extractCursor(data.previous));
        }
    };

    const fetchProductsBySearch = async (query, cursor = null) => {
        setLoading(true);
        const res = await axios.get(`${API_URL}api/selected-product-search/`, { ...getPublicHeaders(), params: { query, cursor: cursor || undefined } });
        setLoading(false);
        if (!res) return;
        const { data } = res;
        if (data) {
            setCategoriesItems(data.results);
            setHasNextPage(!!data.next);
            setHasPreviousPage(!!data.previous);
            setNextCursor(extractCursor(data.next));
            setPrevCursor(extractCursor(data.previous));
        }
    };



    const invalidateCheckoutSession = () => {
        localStorage.removeItem("transaction_id");
    };

    const resetCheckoutSession = async (providedTips, voucherCode) => {
        invalidateCheckoutSession();
        return await fetchCartItemsPrice(providedTips, voucherCode);
    };

    const addToCart = async (item, note, withoutVarientId, addons = []) => {
        try {
            // console.log("addToCart", item)
            setLoading(true);
            const productId = withoutVarientId ? withoutVarientId : item.id;
            const body = { product_id: productId };
            if (addons && addons.length > 0) body.addons = addons;
            await axios.post(`${API_URL}add-to-cart/`, body, getAuthHeaders());
            const localCartitems = localStorage.getItem('cart')
            const items = JSON.parse(localCartitems) || [];
            setCart((prev) => [...prev, item]);
            localStorage.setItem('cart', JSON.stringify([...items, note]));
            invalidateCheckoutSession();
            await fetchCartItemsPrice();
        } catch (error) {
            console.error('Add to cart failed:', error);
            const data = error?.response?.data;
            const message =
                (typeof data?.detail === "string" && data.detail) ||
                (typeof data?.error === "string" && data.error) ||
                (typeof data?.message === "string" && data.message) ||
                (Array.isArray(data) && typeof data[0] === "string" && data[0]) ||
                (data && typeof data === "object" &&
                    Object.values(data).flat().find((v) => typeof v === "string")) ||
                error?.message ||
                "Failed to add item to cart. Please try again.";
            reportContextError("addToCart", error);
            return { error: message };
        } finally {
            setLoading(false);
        }
    };

    const isVoucherValidationError = (error) => {
        const detail = error?.response?.data?.detail || "";
        return (
            typeof detail === "string" &&
            (
                detail.toLowerCase().includes("voucher") ||
                detail.toLowerCase().includes("minimum amount")
            )
        );
    };

    const fetchCartItemsPrice = async (providedTips, voucherCode, allowVoucherRetry = true) => {
        try {
            setLoading(true);
            const localtransaction_id = localStorage.getItem("transaction_id");
            const transaction_id = JSON.parse(localtransaction_id);
            const currentVoucher = (voucherCode !== undefined ? voucherCode : appliedVoucher)?.trim?.() || "";
            const res = await axios.get(`${API_URL}api/cart-summery/`, {
                ...getAuthHeaders(),
                params: {
                    delivery_option: user?.is_pickup_selected ? 'store_pickup' : user?.is_room_delivery ? "room_deliver" : "",
                    transaction_id: transaction_id,
                    voucher_code: currentVoucher || ""
                    // tip_amount: currentTips || 0
                }
            });

            const cartItems = res.data;
            // if (cartItems?.length === 0) {
            //     setTips("");
            // }
            setCartSummery(cartItems);
            const items = await fetchAllCartItems();
            if (!items || items?.length === 0) {
                setAppliedVoucher("")
                setTips("")
                localStorage.removeItem("transaction_id")
                setCartSummery(null)
            }
            return cartItems;
        } catch (error) {
            if (isVoucherValidationError(error)) {
                const voucherError = error?.response?.data?.detail;
                if (appliedVoucher) {
                    setAppliedVoucher("");
                }
                if (allowVoucherRetry) {
                    await fetchCartItemsPrice(providedTips, "", false);
                }
                return { error: voucherError };
            }
            console.error('Fetch Cart Items Error:', error?.response?.data?.detail);
            reportContextError("fetchCartItemsPrice", error);
            return { error: error?.response?.data?.error || error.message };
        } finally {
            setLoading(false);
        }
    };

    const fetchAllCartItems = async () => {
        return dedupeFetch("cartAllItems", async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}cart-items/`, getAuthHeaders());
            // const filterDisableItems =res?.data && res?.data?.cart_items?.filter((item) => item?.is_disabled === false)
            // console.log('notes from localStorage:',res?.data);
            const cartItems = res?.data?.cart_items;
            setCartItemsTotalVat(Number(res?.data?.total_vat || 0));
            const localCartitems = localStorage.getItem("cart");
            const parsedCartItems = JSON.parse(localCartitems) || [];
            const noteMap = new Map(
                parsedCartItems
                    .filter(item => item.note)
                    .map(item => [String(item.product_var), item.note])
            );

            const mergedAndSortedItems = cartItems
                ?.map(item => ({
                    ...item,
                    note: noteMap.get(String(item.product_variant))
                }))
                .sort((a, b) =>
                    (a.product_name || "").localeCompare(b.product_name || "")
                );

            setCartAllitems(mergedAndSortedItems);
            return mergedAndSortedItems;
        } catch (error) {
            console.error('Error fetching cart items:', error);
            reportContextError("fetchAllCartItems", error, "warning");
        } finally {
            setLoading(false);
        }
        });
    };

    const updateCart = async (productId, action) => {
        try {
            setLoading(true);
            await axios.put(
                `${API_URL}update-cart/${productId}?action=${action}`, {},
                getAuthHeaders()
            );
            invalidateCheckoutSession();
            await fetchCartItemsPrice();
            setTips('') // reset tips when cart updated
        } catch (error) {
            console.error('Update cart failed:', error);
            reportContextError("updateCart", error);
        } finally {
            setLoading(false);
        }
    };

    const removeItemFromCart = async (productId, pId) => {
        try {
            setLoading(true);

            // API call to remove from backend cart
            await axios.post(
                `${API_URL}api/remove-from-cart/${productId}/`,
                {},
                getAuthHeaders()
            );

            invalidateCheckoutSession();
            // Refresh cart prices from backend
            await fetchCartItemsPrice();

            // Get local cart items
            const localCartitems = JSON.parse(localStorage.getItem("cart")) || [];

            // Filter out the removed item
            const filteredlocalcartitems = localCartitems.filter(
                (item) => item.product_var !== pId?.product_variant
            );

            // console.log("filteredlocalcartitems", localCartitems, pId, filteredlocalcartitems);

            // Save updated cart back to localStorage
            localStorage.setItem("cart", JSON.stringify(filteredlocalcartitems));
            setTips("") // reset tips when item removed
        } catch (error) {
            console.error("delete cart failed:", error);
            reportContextError("removeItemFromCart", error);
        } finally {
            setLoading(false);
        }
    };

    const updateCartAddon = async (cartAddonId, action) => {
        try {
            setLoading(true);
            await axios.put(
                `${API_URL}cart/addon/${cartAddonId}/?action=${action}`,
                {},
                getAuthHeaders()
            );
            invalidateCheckoutSession();
            await fetchCartItemsPrice();
        } catch (error) {
            console.error('Update cart addon failed:', error);
            reportContextError("updateCartAddon", error);
        } finally {
            setLoading(false);
        }
    };

    const removeCartAddon = async (cartAddonId) => {
        try {
            setLoading(true);
            await axios.delete(
                `${API_URL}cart/addon/remove/${cartAddonId}/`,
                getAuthHeaders()
            );
            invalidateCheckoutSession();
            await fetchCartItemsPrice();
        } catch (error) {
            console.error('Remove cart addon failed:', error);
            reportContextError("removeCartAddon", error);
        } finally {
            setLoading(false);
        }
    };



//     const checkPOSPaymentModes = (paymentModes = [], user) => {
//         if (!user?.pos_status) {
//             return { exists: false, ids: [] };
//         }
// console.log("paymentModes",user.is_pickup_selected , user.is_room_delivery,)
//         const validNames = ["Pay at Store", "Pay at Reception"];

//         const matched = paymentModes?.filter(
//             (mode) =>
//                 validNames?.includes(mode.payment_method_name) &&
//                 mode.is_pos_activated === true,
//         );

//         return {
//             exists: matched?.length > 0, // true if at least one exists & is active
//             ids: matched?.map((m) => m.id), // return their IDs
//         };
//     };
const checkPOSPaymentModes = (paymentModes = [], user) => {
    const hasValidDeliveryOption =
        Boolean(user?.is_pickup_selected || user?.is_room_delivery);

    if (!user?.pos_status || !hasValidDeliveryOption) {
        return { exists: false, ids: [] };
    }

    const validNames = ["Pay at Store", "Pay at Reception"];
    const list = Array.isArray(paymentModes) ? paymentModes : [];

    const matched = list.filter(
        (mode) =>
            validNames.includes(mode.payment_method_name) &&
            mode.is_pos_activated === true
    );

    return {
        exists: matched.length > 0,
        ids: matched.map((m) => m.id),
    };
};
 
const POSResult = checkPOSPaymentModes(paymentMode, user)

    const placeOrder = async (cart, setOpen, setLoading, tableNo, setTableNo, enqueueSnackbar, termnalCheck, setIsTerminalPaymentAvialable, tip_amount, voucherCode, setStockError) => {
        setLoading(true);

        // Construct the payload from cart items
        const products = cart.map(item => ({
            product_id: String(item?.product_variant || item.product), // assuming `product` is the product ID
            order_instruction: item.note || '',
        }));

        try {
            const response = await axios.post(
                `${API_URL}checkout/`,
                {
                    products,
                    number: tableNo,
                    is_pilot_order: user?.is_pilot_active,
                    is_merchant_order: true,
                    tip_amount: tip_amount || 0,
                    voucher_code: voucherCode || appliedVoucher || ""
                },
                getAuthHeaders()
            );

            if (response.status === 201) {
                if (termnalCheck && user?.is_pilot_active === false) {
                    setIsTerminalPaymentAvialable(termnalCheck)
                } else {
                    setOpen(true);
                }
                setLoading(false);
                setTableNo(null);

                localStorage.setItem('transaction_id', JSON.stringify(response?.data?.transaction_id));
                localStorage.setItem('transaction_id_print', JSON.stringify(response?.data?.transaction_id));
                return response?.data?.transaction_id; // Return transaction ID for further processing
            } else {
                localStorage.setItem('transaction_id_print', JSON.stringify(response?.data?.transaction_id));
                localStorage.removeItem('transaction_id');
                setLoading(false);
            }
        } catch (error) {
            const backendError = error?.response?.data?.error;
            
            console.log("return",error,backendError)
            if (backendError) {
                if(backendError==="Some items are out of stock. Please review and update the order."){
                    setStockError(backendError);
                }
                enqueueSnackbar(backendError, { variant: "error" });
                
            } else {
                enqueueSnackbar("Something went wrong. Please try again.", { variant: "error" });
            }

            console.error('Place Order Error:', backendError || error.message);
            reportContextError("placeOrder", error);
            localStorage.removeItem('transaction_id');
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };



    // console.log("paymnetmode", POSResult)
    const proceedToPayment = async (
        {
            delivery_option = user?.is_pickup_selected ? 'store_pickup' : user?.is_room_delivery ? "room_deliver" : "",
            selectedPaymentMode,
            tip_amount

        },
        getorderConfirm,
        setSuccess,
        setLoading,
        setDialogOpen
    ) => {

        // console.log("user2", user?.is_pilot_active)
        const delivery_optionStr = (delivery_option && delivery_option !== "") ? delivery_option : (user?.is_pickup_selected ? 'store_pickup' : user?.is_room_delivery ? "room_deliver" : "");
        const PaymentModeId = selectedPaymentMode ? selectedPaymentMode : POSResult?.ids[0];
        setLoading(true);
        getorderConfirm(false)
        try {
            const localtransaction_id = localStorage.getItem("transaction_id");
            const transaction_id = JSON.parse(localtransaction_id);
            const payload = {
                delivery_option: delivery_optionStr,
                selectedPaymentMode: PaymentModeId,
                transactionId: transaction_id,
                is_merchant_order: true,
                is_pilot_order: user?.is_pilot_active
            };
            const response = await axios.post(
                `${API_URL}proceed-to-payment/`,
                payload,
                getAuthHeaders()
            );

            // console.log('Payment API response:', response.status);

            if (response.status === 200 || response.status === 201) {
                localStorage.removeItem("transaction_id")
                localStorage.removeItem("cart")
                setSuccess(true); // show success animation
                getorderConfirm(true)
                fetchAllCartItems()
                setTips("")
                fetchCartItemsPrice()
            } else {
                console.warn('Payment API responded with unexpected status:', response.status);
            }
        } catch (error) {
            setDialogOpen(false)
              const backendError = error?.response?.data?.error;
                if(backendError==="Some items are out of stock. Please review and update the order."){
                //   enqueueSnackbar(backendError, { variant: "error" });
                }

            console.error('Proceed to Payment Error:', error);
            reportContextError("proceedToPayment", error);
        } finally {
            setLoading(false);
        }
    };
    const proceedToPaymentForsimplepayment = async (
        {
            delivery_option = user?.is_pickup_selected ? 'store_pickup' : user?.is_room_delivery ? "room_deliver" : "",
            selectedPaymentMode,
            tip_amount

        },
        getorderConfirm,
        isPeachpaymentmodeid,
        setPaymentError
    ) => {

        // console.log("user2",isPeachpaymentmodeid)
        const delivery_optionStr = (delivery_option && delivery_option !== "") ? delivery_option : (user?.is_pickup_selected ? 'store_pickup' : user?.is_room_delivery ? "room_deliver" : "");
        // const PaymentModeId = POSResult?.ids[0];
        const PaymentModeId = isPeachpaymentmodeid ? isPeachpaymentmodeid : (selectedPaymentMode ? selectedPaymentMode : POSResult?.ids[0]);
        // setLoading(true);
        // getorderConfirm(false)

        const extractCheckoutUrl = (payload) => {
            const visited = new Set();
            const urlKeys = new Set([
                "checkout_url",
                "checkoutUrl",
                "checkoutURL",
                "zapper_checkout_url",
                "payment_url",
                "paymentUrl",
                "url",
            ]);

            const walk = (value, depth = 0) => {
                if (!value || typeof value !== "object" || depth > 4 || visited.has(value)) {
                    return null;
                }

                visited.add(value);

                for (const key of Object.keys(value)) {
                    const current = value[key];
                    if (urlKeys.has(key) && typeof current === "string" && current.trim()) {
                        return current.trim();
                    }
                }

                for (const key of Object.keys(value)) {
                    const current = value[key];
                    if (current && typeof current === "object") {
                        const found = walk(current, depth + 1);
                        if (found) return found;
                    }
                }

                return null;
            };

            return walk(payload);
        };

        try {
            const localtransaction_id = localStorage.getItem("transaction_id");
            const transaction_id = JSON.parse(localtransaction_id);
            const payload = {
                delivery_option: delivery_optionStr,
                selectedPaymentMode: PaymentModeId,
                transactionId: transaction_id,
                is_merchant_order: true,
                is_pilot_order: user?.is_pilot_active
            };
            const response = await axios.post(
                `${API_URL}proceed-to-payment/`,
                payload,
                getAuthHeaders()
            );

            // console.log('Payment API response:', response.status);

            if (response.status === 200 || response.status === 201) {
                // localStorage.removeItem("cart")
                // setSuccess(true); // show success animation
                // getorderConfirm(true)
                // fetchAllCartItems()
                // fetchCartItemsPrice()
                const ispeachQR = response?.data?.peach_pay_qr
                const checkoutUrl = extractCheckoutUrl(response?.data);

                return { orderplaced: true, ispeachQR, checkoutUrl }
            } else {
                console.warn('Payment API responded with unexpected status:', response.status);
            }
        } catch (error) {
            const backendError = error?.response?.data?.error || error?.response?.data?.detail || "Payment failed. Please try again.";
            if (setPaymentError) setPaymentError(backendError);
            console.error('Proceed to Payment Error:', error);
            reportContextError("proceedToPaymentForsimplepayment", error);
        } finally {
            // setLoading(false);
        }
    };

    const UpdatedTerminalOrderStatus = async (payBy, orderNotes, setIsTerminalPaymentAvialable, setShowPopup, getOrderList, pos = false) => {
        try {
            const localtransaction_id = localStorage.getItem("transaction_id");
            const transaction_id = JSON.parse(localtransaction_id);

            const response = await axiosInstance.put(APIRouteConstants.STORE.UPDATE_PAYMENT_STATUS, {
                transaction_id: transaction_id,
                pay_by: payBy,
                order_notes: orderNotes,
                pos: pos
            });

            if (response.status === 200 || response.status === 201) {
                //  console.log("proceedToPaymentForsimplepayment",response)

                localStorage.removeItem("cart")
                localStorage.removeItem("transaction_id")

                // setSuccess(true); // show success animation
                // getorderConfirm(true)
                fetchAllCartItems()
                setTips("")
                fetchCartItemsPrice(undefined, undefined)

                setShowPopup(true)
                setTimeout(() => {
                    getOrderList?.(true)
                }, 4000);

            } else {
                console.warn('Payment API responded with unexpected status:', response.status);
            }
        }
        catch (err) {
            console.error('Update terminal order status failed:', err);
            reportContextError("UpdatedTerminalOrderStatus", err);
        }
    }

    const proceedToPaymentAPIforTerminal = async (
        {
            delivery_option = user?.is_pickup_selected ? 'store_pickup' : user?.is_room_delivery ? "room_deliver" : "",
            selectedPaymentMode,
            tip_amount

        },
        terminalpaymentid,
        terminal_ID,
        setPaymentDialogStatus,
        transaction_idfromorderlist, ismerchantOrder
    ) => {
        setisTerminalOnline(null)
        // console.log("user2", user?.is_pilot_active)
        const delivery_optionStr = (delivery_option && delivery_option !== "") ? delivery_option : (user?.is_pickup_selected ? 'store_pickup' : user?.is_room_delivery ? "room_deliver" : "");
        const PaymentModeId = terminalpaymentid ? terminalpaymentid : selectedPaymentMode;
        // setLoading(true);
        //    console.log("POSResult",POSResult)
        try {
            const localtransaction_id = localStorage.getItem("transaction_id");
            const transaction_id = JSON.parse(localtransaction_id);
            const payload = {
                delivery_option: delivery_optionStr,
                selectedPaymentMode: PaymentModeId,
                transactionId: transaction_idfromorderlist ? transaction_idfromorderlist : transaction_id,
                is_merchant_order: ismerchantOrder === false ? ismerchantOrder : true,
                is_pilot_order: ismerchantOrder === false ? ismerchantOrder : user?.is_pilot_active
            };

            // console.log('placeOrder f', transaction_id);
            const response = await axios.post(
                `${API_URL}proceed-to-payment/`,
                payload,
                getAuthHeaders()
            );

            // console.log('Payment API response:', response.status);

            if (response.status === 200 || response.status === 201) {

                localStorage.removeItem("cart")
                // setSuccess(true); // show success animation

                SticittTerminalProceedtoPayment(response, terminal_ID, setPaymentDialogStatus)
            } else {
                console.warn('Payment API responded with unexpected status:', response.status);
            }
        } catch (error) {
            console.error('Proceed to Payment Error:', error);
            reportContextError("proceedToPaymentAPIforTerminal", error);
        } finally {
            // setLoading(false);
        }
    };

    const checkOrderStatus = async (orderlist = false) => {
        try {
            console.log("orderlist", orderlist)
            const localtransaction_id = orderlist ? localStorage.getItem("OrderlistTXID") : localStorage.getItem("transaction_id");

            let transaction_id;
            try {
                transaction_id = JSON.parse(localtransaction_id);
            } catch (e) {
                // If parse fails (e.g. raw string "webbie-123"), use original string
                transaction_id = localtransaction_id;
            }

            // console.log("Checking order status for ID:", transaction_id);
            console.log("orderlist id", transaction_id)

            if (!transaction_id) return null;

            // Updated generic GET request (axiosInstance usually handles BASE_URL)
            // Using params for query string ?transaction_id=...
            const res = await axiosInstance.get(
                APIRouteConstants.STORE.PAYMENT_STATUS, {
                params: {
                    transaction_id: transaction_id
                }
            }
            );

            // User provided response: { "data": { "transaction_id": "...", "payment_status": "pending" } }
            // Axios response structure: res.data is the body.
            // So we access res.data.data.payment_status

            if (res.data && res.data.data) {
                // Return the payment/order status
                // console.log("Order Status Response:", res.data.data.payment_status);
                return res.data.data.payment_status;
            }
            return null;

        } catch (error) {
            console.error("Check Order Status Error:", error);
            reportContextError("checkOrderStatus", error, "warning");
            return null;
        }
    };
    const getTransactionDetail = async (transaction_id) => {
        try {
            const res = await axios.get(
                `${API_URL}transaction-detail/?transaction_id=${transaction_id}`,
                getAuthHeaders()
            );
            localStorage.removeItem("transaction_id_print")
            console.log("Transaction Detail Response:", res.data);
            return res.data;
        } catch (error) {
            localStorage.removeItem("transaction_id_print")
            console.error("Get Transaction Detail Error:", error);
            reportContextError("getTransactionDetail", error);
            return null;
        }
    };

    const cancelSticittPayment = async (transaction_idfromorderlist) => {
        try {
            const localtransaction_id = localStorage.getItem("transaction_id");
            const transaction_id = JSON.parse(localtransaction_id);
            const payload = {
                transactionId: transaction_idfromorderlist ? transaction_idfromorderlist : transaction_id,
            };
            const response = await axios.post(
                `${API_URL}sticitt_pay/cancel-sticitt-payment/`,
                payload,
                getAuthHeaders()
            );
            if (response.status === 200 || response.status === 201) {

                localStorage.removeItem("cart")
                // setSuccess(true); // show success animation

                // SticittTerminalProceedtoPayment(response, terminal_ID, setPaymentDialogStatus)
            } else {
                console.warn('Payment API responded with unexpected status:', response.status);
            }

        } catch (err) {
            console.log("err", err)
            reportContextError("cancelSticittPayment", err);
        }
    }

    // Categories, payment methods, terminals, Zapper credentials, cart and
    // tip settings are only ever consumed on the dashboard/POS screen
    // (Home) — fetching them here unconditionally meant every route fired
    // them, even pages that never read this data. EcommerceProvider is
    // mounted above <BrowserRouter> (see index.js) so it has no way to
    // check the current route itself; Home triggers this fetch on its own
    // mount instead, since it only mounts while actually on that route.
    useEffect(() => {
        const savedToken = token || localStorage.getItem('authToken');
        if (user?.websiteUrl) setToken(savedToken);
    }, [user?.websiteUrl]);




    useEffect(() => {
        const existingAccessToken = localStorage.getItem('access-token'); // used for merchant login
        const existingAuthToken = localStorage.getItem('authToken'); // used for customer API access

        if (!existingAuthToken && existingAccessToken) {
            // Step 1: Call login to get customer-facing token
            login().then(() => {
                getUser(); // Step 2: fetch user with new auth token

            });
        } else if (existingAccessToken) {
            login().then(() => {
                getUser(); // Step 2: fetch user with new auth token

            });
        }
    }, []);
    return (
        <EcommerceContext.Provider
            value={{
                user,
                getUser,
                login,
                logout,
                menuItems,
                isTipFeatureEnabled,
                setIsTipFeatureEnabled,
                isTipFeatureEnabledPOS,
                setIsTipFeatureEnabledPOS,
                tips,
                setTips,
                categories,
                categoriesFailed,
                retryCategories,
                categoriesItems,
                fetchCategories,
                fetchCategoriesItemsByname,
                fetchProductsBySearch,
                cart,
                cartSummery,
                cartAllitems,
                cartItemsTotalVat,
                updateCart,
                addToCart,
                orders,
                placeOrder,
                proceedToPayment,
                proceedToPaymentAPIforTerminal,
                removeItemFromCart,
                selectedCategory,
                setSelectedCategory,
                loading,
                paymentMode,
                POSResult,
                terminals,
                setTerminals,
                SticittTerminalProceedtoPayment,
                getTerminalList,
                getPaymentMethods, selectedTerminal, setSelectedTerminals, proceedToPaymentForsimplepayment,
                hasNextPage, hasPreviousPage, nextCursor, prevCursor, UpdatedTerminalOrderStatus,
                fetchAllCartItems,
                fetchCartItemsPrice, isTerminalOnline,
                sticittTerminals, setSticittTerminals, cancelSticittPayment, socketData, setSocketData, saveLastUsedTerminal,
                checkOrderStatus,
                getTransactionDetail,
                fetchTipSettings,
                updateTipSettings,
                merchantName, setMerchantName,
                appliedVoucher, setAppliedVoucher,
                resetCheckoutSession,
                vatRegNo, setVatRegNo,
                zapperCredential,
                getZapperPaymentCredentials,
                zapperStatusUpdate,
                zapperSaveData,
                updateCartAddon,
                removeCartAddon,
            }}
        >
            {children}
        </EcommerceContext.Provider>
    );
};


// POST/customer/checkout/
// GET/accounts/payment-methods/
// POST/customer/proceed-to-payment/
// GET/sticitt_pay/terminal-list/
// POST/sticitt_pay/sticitt_terminal_payment/

// Kumari roji, Yesterday 11:28 AM
// GET/sticitt_pay/terminal-list/
// POST/sticitt_pay/sticitt_terminal_payment/  ye do app avi dekh nahi sakte branch change kardiya hai beta p
// {
//     "terminal_id": "f94efea8-eec8-417d-a982-90939691895f",
//     "payment_id": "05ee3098-9ee8-46ac-ae0e-cf5a5c73e47e"

// }
// for status webosckrt url
// wss://https://beta-api.webbieshop.com/ws/orders/merchantid/
// merchantid is merchat id
// You, Yesterday 11:29 AM
// Ok

// Kumari roji, Yesterday 11:30 AM
// get payment s terminal ids mil jayenge merchant jo select karega uski id pass hogi
// proceed to pay m fir sticitt k case m payment id milegi or peach k case m qr code


// for peach terminal 
